import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAllPoints,
  selectEditorNode,
} from '../../map/slice/mapSelectors';
import { NodePoints } from '../../nodes/NodePoints';
import { CheckIcon } from '@iconicicons/react';
import { EditorMode, EditorNode } from '../slice/mapSlice';
import { Point } from '@/types';
import { Coordinate } from 'ol/coordinate';

export type PopupMapEditorProps = {
  onCreate: (data: EditorNode) => void;
  onCreateStart?: () => void;
  onCreateCancel?: () => void;
  onAddPoint?: (pointId: number) => void;
  onRemovePoint?: (pointId: number) => void;
  isLoading?: boolean;
  coords: Coordinate;
  mode?: EditorMode;
};

export const PopupMapEditor: React.FC<PopupMapEditorProps> = ({
  onCreate,
  onCreateStart,
  onCreateCancel,
  onAddPoint,
  onRemovePoint,
  isLoading = false,
  coords,
  mode = 'none',
}) => {
  const allPoints = useSelector(selectAllPoints);

  const newNode = useSelector(selectEditorNode);
  const isCreatingNode = Boolean(newNode);

  const newNodePoints = useMemo<Point[]>(
    () =>
      newNode?.points?.map((pointId) => allPoints[pointId]).filter(Boolean) ??
      [],
    [newNode?.points],
  );

  useEffect(() => {
    const creationMode = mode === 'shift';
    if (creationMode) {
      onCreateStart?.();
    } else if (isCreatingNode) {
      onCreateCancel?.();
    }
  }, [coords[0], coords[1]]);

  const addNodePoint = (id: string | number) => {
    const point = allPoints[id];
    if (!isCreatingNode || !point) return;
    if (
      point.node !== null &&
      point.node !== undefined &&
      confirm(
        'You are trying to add a point that is already assigned to another node. This will remove the point from the previous node, proceed?',
      ) === false
    ) {
      return;
    }
    onAddPoint?.(Number(id));
  };

  const removeNodePoint = (id: string | number) => {
    onRemovePoint?.(Number(id));
  };

  const tsps = useMemo(
    () =>
      newNode?.tspId
        ? [Number(newNode.tspId)]
        : newNodePoints.map((point) => point.tsp_id) ?? [],
    [newNodePoints, newNode?.tspId],
  );

  return isCreatingNode ? (
    <div className="px-3" style={{ minWidth: 400 }}>
      <NodePoints
        lockedTsps={tsps}
        points={newNodePoints}
        onAddPoint={addNodePoint}
        onRemovePoint={removeNodePoint}
        editable={!isLoading}
      />
      {newNodePoints?.length === 0 && (
        <p className="mb-0 text-center">No points</p>
      )}
      <button
        className="btn btn-dark float-end mt-2"
        disabled={!newNodePoints?.length || isLoading}
        role="button"
        onClick={() => {
          if (!newNode) return;
          onCreate?.(newNode);
        }}
      >
        <CheckIcon />
      </button>
    </div>
  ) : (
    <div className="px-3">
      <span className="mb-0" role="button" onClick={onCreateStart}>
        Add point
      </span>
    </div>
  );
};
